import React from 'react'
import './App.css'

function App() {
  return (
    <div className="portfolio">
      {/* Header */}
      <header className="header">
        <nav className="nav">
          <div className="nav-content">
            <h1 className="nav-logo">Laith Reece</h1>
            <div className="nav-links">
              <a href="#about">About</a>
              <a href="#skills">Skills</a>
              <a href="#projects">Projects</a>
              <a href="#contact">Contact</a>
            </div>
          </div>
        </nav>
        
        <div className="hero">
          <div className="hero-content">
            <h1 className="hero-title">Laith Jamal Reece</h1>
            <h2 className="hero-subtitle">GRC Engineer | IT Risk Management Analyst</h2>
            <p className="hero-location">DeSoto, TX | Open to Remote</p>
            <p className="hero-description">
              Specializing in automating PCI DSS 4.0.1 and NIST CSF controls across large-scale cloud environments. 
              I design continuous compliance pipelines using AWS, ServiceNow GRC, and Python to reduce audit fatigue 
              and keep regulated systems audit-ready year-round.
            </p>
            <div className="hero-actions">
              <a href="mailto:laithjreece@gmail.com" className="btn btn-primary">Contact Me</a>
              <a href="#projects" className="btn btn-secondary">View Projects</a>
            </div>
          </div>
        </div>
      </header>

      {/* About Section */}
      <section id="about" className="section">
        <div className="container">
          <h2 className="section-title">About Me</h2>
          <div className="about-content">
            <div className="about-text">
              <p>
                I'm a GRC Engineer and IT Risk Management Analyst specializing in automating PCI DSS 4.0.1 and NIST CSF controls across large-scale cloud environments. I design continuous compliance pipelines using AWS, ServiceNow GRC, and Python to reduce audit fatigue, improve control visibility, and keep regulated systems audit-ready year-round.
              </p>
              <p>
                Over the past decade in financial services, I've led initiatives that raised enterprise PCI compliance from 82% to 92%, reduced global outages by over 80%, and cut audit preparation time by 60% by replacing manual evidence collection with automated control validation.
              </p>
              <p>
                I'm driven by the belief that GRC should be engineered—not documented. When controls are measurable, automated, and integrated into delivery pipelines, security becomes a business enabler instead of a bottleneck.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="section section-alt">
        <div className="container">
          <h2 className="section-title">Skills & Expertise</h2>
          <div className="skills-grid">
            <div className="skill-category">
              <h3>Cloud Platforms</h3>
              <ul>
                <li>AWS</li>
              </ul>
            </div>
            <div className="skill-category">
              <h3>Security & Compliance</h3>
              <ul>
                <li>NIST CSF</li>
                <li>PCI DSS 4.0.1</li>
                <li>Continuous Compliance & Audit Readiness</li>
              </ul>
            </div>
            <div className="skill-category">
              <h3>Tools & Technologies</h3>
              <ul>
                <li>CloudFormation</li>
                <li>AWS Security Hub</li>
                <li>GuardDuty</li>
                <li>CloudTrail</li>
                <li>AWS Config (Custom Rules)</li>
              </ul>
            </div>
            <div className="skill-category">
              <h3>Programming/Scripting</h3>
              <ul>
                <li>Python</li>
                <li>SQL</li>
                <li>Infrastructure as Code (CloudFormation)</li>
              </ul>
            </div>
            <div className="skill-category">
              <h3>GRC Frameworks</h3>
              <ul>
                <li>PCI DSS 4.0.1</li>
                <li>NIST CSF</li>
                <li>Continuous Control Monitoring</li>
              </ul>
            </div>
            <div className="skill-category">
              <h3>Other Skills</h3>
              <ul>
                <li>Risk Assessment</li>
                <li>Security Auditing</li>
                <li>Policy Development</li>
                <li>Incident Response</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="section">
        <div className="container">
          <h2 className="section-title">Featured Projects</h2>
          <div className="projects-grid">
            <div className="project-card">
              <h3>PCI Compliance Automation Framework</h3>
              <p>
                Built an automated compliance monitoring system that reduced audit preparation time by 60% 
                and improved enterprise PCI compliance from 82% to 92% through continuous control validation.
              </p>
              <div className="project-tech">
                <span className="tech-tag">AWS Lambda</span>
                <span className="tech-tag">Python</span>
                <span className="tech-tag">CloudFormation</span>
                <span className="tech-tag">Security Hub</span>
              </div>
              <div className="project-achievements">
                <h4>Key Achievements:</h4>
                <ul>
                  <li>Reduced audit prep time by 60%</li>
                  <li>Increased PCI compliance to 92%</li>
                  <li>Automated evidence collection</li>
                </ul>
              </div>
            </div>

            <div className="project-card">
              <h3>Cloud Security Posture Management</h3>
              <p>
                Developed a comprehensive security monitoring solution using AWS Security Hub and custom 
                Config rules to provide real-time visibility into security compliance across multi-account environments.
              </p>
              <div className="project-tech">
                <span className="tech-tag">AWS Security Hub</span>
                <span className="tech-tag">AWS Config</span>
                <span className="tech-tag">Python</span>
                <span className="tech-tag">CloudTrail</span>
              </div>
              <div className="project-achievements">
                <h4>Key Achievements:</h4>
                <ul>
                  <li>Reduced global outages by 80%</li>
                  <li>Real-time compliance monitoring</li>
                  <li>Multi-account visibility</li>
                </ul>
              </div>
            </div>

            <div className="project-card">
              <h3>Continuous Control Monitoring System</h3>
              <p>
                Implemented a continuous control monitoring framework that integrates with ServiceNow GRC 
                to provide automated validation of security controls and real-time risk assessment.
              </p>
              <div className="project-tech">
                <span className="tech-tag">ServiceNow GRC</span>
                <span className="tech-tag">Python</span>
                <span className="tech-tag">AWS</span>
                <span className="tech-tag">SQL</span>
              </div>
              <div className="project-achievements">
                <h4>Key Achievements:</h4>
                <ul>
                  <li>Eliminated manual evidence collection</li>
                  <li>24/7 control monitoring</li>
                  <li>Integrated risk reporting</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section id="certifications" className="section section-alt">
        <div className="container">
          <h2 className="section-title">Certifications</h2>
          <div className="certifications">
            <div className="certification-card">
              <h3>AWS Certified Cloud Practitioner</h3>
              <p>Amazon Web Services</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section">
        <div className="container">
          <h2 className="section-title">Get In Touch</h2>
          <div className="contact-content">
            <p>
              I'm always interested in discussing new opportunities, challenging projects, or collaborations 
              in the GRC and cloud security space. Feel free to reach out!
            </p>
            <div className="contact-methods">
              <div className="contact-method">
                <h3>Email</h3>
                <a href="mailto:laithjreece@gmail.com">laithjreece@gmail.com</a>
              </div>
              <div className="contact-method">
                <h3>LinkedIn</h3>
                <a href="https://linkedin.com/in/laithreece" target="_blank" rel="noopener noreferrer">linkedin.com/in/laithreece</a>
              </div>
              <div className="contact-method">
                <h3>GitHub</h3>
                <a href="https://github.com/laithjreece-svg" target="_blank" rel="noopener noreferrer">github.com/laithjreece-svg</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>&copy; 2024 Laith Jamal Reece. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}

export default App
